import { useState, useEffect, useContext, useMemo } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import { modalContext } from '@contexts'
import {
  IntegrationAssignedFilter,
  AddFilter,
  IntegrationFilterValue,
  IntegrationFilter,
  DATE_FILTER_FORMAT,
  FiltersState,
  getOperatorByFilterType,
  RequiredDateFilterDefault,
  FilterRenderer,
} from './components'

const FullWidth = styled.div`
  width: 100%;
`
const NoMarginTypography = styled(Typography)`
  margin-top: 0px;
`
const TypographyBold = styled(Typography)`
  weight: 500;
`

interface Props {
  filters: IntegrationAssignedFilter[]
  integrationFilterOptions: IntegrationFilter[]
  onFiltersChange: (filters: IntegrationAssignedFilter[]) => void
  filtersState: FiltersState
  onDataResetChange: (filters: IntegrationAssignedFilter[], reset: boolean) => void
  confirmDataReset: boolean
  requiredDateFilterDefaults?: RequiredDateFilterDefault[]
  previousYearsRestriction?: number
  integrationSlug?: string
  connectorId?: string
}

export const IntegrationFilters = ({
  filters,
  integrationFilterOptions,
  onFiltersChange,
  filtersState,
  onDataResetChange,
  confirmDataReset,
  requiredDateFilterDefaults,
  previousYearsRestriction,
  integrationSlug,
  connectorId,
}: Props) => {
  const ModalContext = useContext(modalContext)
  const [resetConfirmed, setResetConfirmed] = useState<boolean>(false)
  const [initializedFilters, setInitializedFilters] = useState<boolean>(false)
  const [assignedFilters, setAssignedFilters] = useState<IntegrationAssignedFilter[]>(filters)

  useEffect(() => {
    if (resetConfirmed) {
      onDataResetChange(assignedFilters, true)
    } else if (initializedFilters) {
      onFiltersChange(assignedFilters)
    }
  }, [assignedFilters, resetConfirmed, initializedFilters])

  useEffect(() => {
    setRequiredFilters()
  }, [integrationFilterOptions])

  function setRequiredFilters() {
    integrationFilterOptions.forEach(option => {
      if (
        option.required &&
        filters.filter(i => i.integrationFilterId == option.id).length == 0 &&
        assignedFilters.filter(i => i.integrationFilterId == option.id).length == 0
      ) {
        const defaultValues: IntegrationFilterValue[] = []

        const requiredFilterDefault = requiredDateFilterDefaults?.find(
          d => d.key == option.filterKey,
        )

        if (requiredFilterDefault) {
          const defaultValue: IntegrationFilterValue = {
            display_name: requiredFilterDefault.display_name,
            value: requiredFilterDefault.value,
          }
          defaultValues.push(defaultValue)
        }

        const assignedFilter: IntegrationAssignedFilter = {
          integrationFilterId: option.id,
          values: defaultValues,
          operator: requiredFilterDefault
            ? requiredFilterDefault.operator
            : getOperatorByFilterType(option),
        }

        const newAssignedFilters = assignedFilters.concat([assignedFilter])
        setAssignedFilters(newAssignedFilters)
      }
    })

    setInitializedFilters(true)
  }

  function removeAssignedFilter(assignedFilter: IntegrationAssignedFilter): void {
    const newFilters = assignedFilters.filter(
      f => f.integrationFilterId !== assignedFilter.integrationFilterId,
    )

    if (confirmDataReset) {
      showIntegrationDataResetWarning(() => {
        setResetConfirmed(true)
        setAssignedFilters(newFilters)
      })
    } else {
      setAssignedFilters(newFilters)
    }
  }

  function assignFilter(filter: IntegrationFilter): void {
    const newFilter: IntegrationAssignedFilter = {
      integrationFilterId: filter.id,
      values: [],
      operator: getOperatorByFilterType(filter),
    }
    if (filter.filterType == 'date') {
      const now = moment().format(DATE_FILTER_FORMAT)
      const dateValue: IntegrationFilterValue = {
        value: now,
        display_name: now,
      }
      newFilter.values.push(dateValue)
    }
    const newFilters = assignedFilters.concat([newFilter])

    if (confirmDataReset) {
      showIntegrationDataResetWarning(() => {
        setResetConfirmed(true)
        setAssignedFilters(newFilters)
      })
    } else {
      setAssignedFilters(newFilters)
    }
  }

  function handleFilterChange(values: IntegrationFilterValue[], filter: IntegrationAssignedFilter) {
    const newFilters = assignedFilters.map(f => {
      if (f.integrationFilterId == filter.integrationFilterId) {
        const updatedFilter: IntegrationAssignedFilter = {
          ...f,
          operator: filter.operator,
          values,
        }
        return updatedFilter
      }
      return f
    })

    if (confirmDataReset) {
      showIntegrationDataResetWarning(() => {
        setResetConfirmed(true)
        setAssignedFilters(newFilters)
      })
    } else {
      setAssignedFilters(newFilters)
    }

    return Promise.resolve()
  }

  function showIntegrationDataResetWarning(confirmAction: any) {
    ModalContext.displayModal({
      title: t`Are you sure?`,
      description: t`This integration has already synced data to Claravine, changing the current integration filters will cause a full reset of the data for this template.`,
      confirm: () => () => {
        confirmAction()
      },
    })
  }

  const filtersWithOptions = useMemo(
    () =>
      integrationFilterOptions.map(filterOption => ({
        filter: assignedFilters.find(
          assignedFilter => assignedFilter.integrationFilterId == filterOption.id,
        ),
        filterOption,
      })),
    [assignedFilters, integrationFilterOptions],
  )

  const filtersToRender = useMemo(
    () =>
      filtersWithOptions
        .filter(({ filter }) => !!filter)
        .sort(a => (a.filterOption.required ? -1 : 1)) as {
        filter: IntegrationAssignedFilter
        filterOption: IntegrationFilter
      }[],
    [filtersWithOptions],
  )

  const availableFilterOptions = useMemo(
    () =>
      filtersWithOptions.filter(({ filter }) => !filter).map(({ filterOption }) => filterOption),
    [filtersWithOptions],
  )

  return (
    <FullWidth>
      <TypographyBold variant="h4">{t`Integration filters`}</TypographyBold>
      <NoMarginTypography variant="body2">
        {t`Apply filters below to limit the data synced to this template`}
      </NoMarginTypography>
      {filtersToRender.map(({ filter, filterOption }, index) => (
        <FilterRenderer
          key={`int-filter-${index}-${filter.integrationFilterId}`}
          connectorId={connectorId}
          filter={filter}
          filterOption={filterOption}
          onFilterChange={handleFilterChange}
          onFilterDelete={removeAssignedFilter}
          previousYearsRestriction={previousYearsRestriction}
          integrationSlug={integrationSlug}
        />
      ))}
      <AddFilter
        availableFilters={availableFilterOptions}
        assignFilter={assignFilter}
        filtersState={filtersState}
      />
    </FullWidth>
  )
}
